import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Brand from '../components/Brand';
import { validateIdentifier, validatePassword } from '../utils/validators';

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState('email');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const runValidation = () => {
    const next = {};
    const idErr = validateIdentifier(method, identifier);
    if (idErr) next.identifier = idErr;
    const pwErr = validatePassword(password);
    if (pwErr) next.password = pwErr;
    return next;
  };

  const clearFieldError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = runValidation();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 400));
    onLogin({
      method,
      identifier: identifier.trim(),
      loggedInAt: new Date().toISOString(),
    });
    setSubmitting(false);
    navigate('/home');
  };

  const switchMethod = (next) => {
    setMethod(next);
    setIdentifier('');
    clearFieldError('identifier');
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Brand />
        <h1 className="auth-title">Welcome back</h1>
        <p className="auth-subtitle">Sign in to access your Bluprint</p>

        <div className="tab-switcher">
          <button
            type="button"
            className={`tab-btn ${method === 'email' ? 'active' : ''}`}
            onClick={() => switchMethod('email')}
          >
            Email
          </button>
          <button
            type="button"
            className={`tab-btn ${method === 'phone' ? 'active' : ''}`}
            onClick={() => switchMethod('phone')}
          >
            Phone
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="login-identifier">
              {method === 'email' ? 'Email address' : 'Phone number'}
              <span className="required-star"> *</span>
            </label>
            <input
              id="login-identifier"
              className={`form-input ${errors.identifier ? 'input-error' : ''}`}
              type={method === 'email' ? 'email' : 'tel'}
              autoComplete={method === 'email' ? 'email' : 'tel'}
              placeholder={method === 'email' ? 'you@example.com' : '+1 555 000 0000'}
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                clearFieldError('identifier');
              }}
              aria-invalid={!!errors.identifier}
            />
            {errors.identifier && <div className="field-error">{errors.identifier}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="login-password">
              Password<span className="required-star"> *</span>
            </label>
            <input
              id="login-password"
              className={`form-input ${errors.password ? 'input-error' : ''}`}
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearFieldError('password');
              }}
              aria-invalid={!!errors.password}
            />
            {errors.password && <div className="field-error">{errors.password}</div>}
            <Link to="/forgot-password" className="forgot-link">
              Forgot password?
            </Link>
          </div>

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="auth-links">
          Don't have an account? <Link to="/signup">Sign up</Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
